import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Plus, Clock, User, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, startOfWeek, isSameDay } from 'date-fns';

export default function CalendarPage() {
  const { api, user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [rehabAssistants, setRehabAssistants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    patient_id: '',
    therapist_id: user?.id || '',
    rehab_assistant_id: '',
    appointment_type: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '09:00',
    end_time: '10:00',
    notes: ''
  });

  const [accessLevel, setAccessLevel] = useState(null);

  useEffect(() => {
    fetchData();
    fetchAccessLevel();
  }, [selectedDate]);

  const fetchAccessLevel = async () => {
    try {
      const response = await api.get('/access-control/user-access-level');
      setAccessLevel(response.data);
    } catch (error) {
      console.error('Failed to fetch access level:', error);
    }
  };

  const fetchData = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const [appointmentsRes, patientsRes, therapistsRes, rehabAssistantsRes] = await Promise.all([
        api.get('/appointments', { params: { date: dateStr } }),
        api.get('/patients'),
        api.get('/staff/therapists'),
        api.get('/staff/rehab-assistants')
      ]);
      setAppointments(appointmentsRes.data);
      setPatients(patientsRes.data);
      setTherapists(therapistsRes.data);
      setRehabAssistants(rehabAssistantsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/appointments', formData);
      toast.success('Appointment scheduled successfully');
      setDialogOpen(false);
      fetchData();
      setFormData({
        patient_id: '',
        therapist_id: user?.id || '',
        appointment_type: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        start_time: '09:00',
        end_time: '10:00',
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create appointment');
    }
  };

  const timeSlots = Array.from({ length: 12 }, (_, i) => {
    const hour = i + 8;
    return `${hour.toString().padStart(2, '0')}:00`;
  });

  const getAppointmentsForSlot = (time) => {
    return appointments.filter(apt => apt.start_time === time);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f]">Calendar</h1>
          <p className="text-slate-500 mt-1">
            {accessLevel?.can_view_all_appointments 
              ? 'Viewing all appointments' 
              : 'Viewing your assigned children\'s appointments'}
          </p>
          {accessLevel && !accessLevel.can_view_all_appointments && (
            <Badge variant="outline" className="mt-1 text-xs">
              <User className="w-3 h-3 mr-1" />
              {accessLevel.is_lead_therapist ? 'Lead Therapist' : 
               accessLevel.role === 'reception' ? 'Reception' :
               accessLevel.role === 'rehab_assistant' ? 'Rehab Assistant' : 'Therapist'} View
            </Badge>
          )}
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 rounded-full" data-testid="new-appointment-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Appointment
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-[#1e3a5f]">Schedule Appointment</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Patient *</Label>
                <Select
                  value={formData.patient_id}
                  onValueChange={(value) => setFormData({ ...formData, patient_id: value })}
                >
                  <SelectTrigger data-testid="appointment-patient">
                    <SelectValue placeholder="Select patient..." />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.first_name} {p.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Therapist *</Label>
                <Select
                  value={formData.therapist_id}
                  onValueChange={(value) => setFormData({ ...formData, therapist_id: value })}
                >
                  <SelectTrigger data-testid="appointment-therapist">
                    <SelectValue placeholder="Select therapist..." />
                  </SelectTrigger>
                  <SelectContent>
                    {therapists.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.first_name} {t.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Rehab Assistant (Optional)</Label>
                <Select
                  value={formData.rehab_assistant_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, rehab_assistant_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="appointment-rehab-assistant">
                    <SelectValue placeholder="Select rehab assistant..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- No Rehab Assistant --</SelectItem>
                    {rehabAssistants.map((ra) => (
                      <SelectItem key={ra.id} value={ra.id}>
                        {ra.first_name} {ra.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Appointment Type *</Label>
                <Select
                  value={formData.appointment_type}
                  onValueChange={(value) => setFormData({ ...formData, appointment_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="initial_assessment">Initial Assessment</SelectItem>
                    <SelectItem value="therapy_session">Therapy Session</SelectItem>
                    <SelectItem value="review">Review</SelectItem>
                    <SelectItem value="follow_up">Follow Up</SelectItem>
                    <SelectItem value="discharge">Discharge</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Start Time *</Label>
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time *</Label>
                  <Input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90" data-testid="appointment-submit">
                  Schedule
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendar Sidebar */}
        <Card className="card-icrcl">
          <CardContent className="p-4">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              className="rounded-md"
            />
          </CardContent>
        </Card>

        {/* Day View */}
        <Card className="lg:col-span-3 card-icrcl">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg text-[#1e3a5f]">
                {format(selectedDate, 'EEEE, MMMM d, yyyy')}
              </CardTitle>
              <CardDescription>
                {appointments.length} appointments scheduled
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setSelectedDate(addDays(selectedDate, -1))}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedDate(new Date())}
              >
                Today
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setSelectedDate(addDays(selectedDate, 1))}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
            ) : (
              <div className="space-y-2">
                {timeSlots.map((time) => {
                  const slotAppointments = getAppointmentsForSlot(time);
                  return (
                    <div key={time} className="flex gap-4 min-h-[60px]">
                      <div className="w-16 text-sm text-slate-500 font-medium pt-2">
                        {time}
                      </div>
                      <div className="flex-1 border-l border-slate-200 pl-4">
                        {slotAppointments.length > 0 ? (
                          <div className="space-y-2">
                            {slotAppointments.map((apt) => (
                              <div
                                key={apt.id}
                                className="p-3 rounded-lg bg-[#2a9d8f]/10 border-l-4 border-[#2a9d8f]"
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium text-slate-900">
                                      {apt.patient_name}
                                    </p>
                                    <p className="text-sm text-slate-500">
                                      {apt.appointment_type.replace(/_/g, ' ')} • {apt.therapist_name}
                                      {apt.rehab_assistant_name && (
                                        <span className="text-cyan-600"> + {apt.rehab_assistant_name}</span>
                                      )}
                                    </p>
                                  </div>
                                  <Badge className={
                                    apt.status === 'completed' ? 'badge-completed' :
                                    apt.status === 'confirmed' ? 'badge-active' :
                                    apt.status === 'cancelled' ? 'badge-cancelled' :
                                    'badge-pending'
                                  }>
                                    {apt.status}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="h-full border-b border-dashed border-slate-100"></div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
